import { useTranslation } from 'react-i18next';
import type { AlertSummaryEntry } from 'src/queries';
import './AlertBadge.css';

interface AlertBadgeProps {
  // The open-alert summary for one installation, or undefined when there are
  // no open alerts.
  summary?: AlertSummaryEntry;
}

// Map worst open severity -> a CSS modifier. Unknown/absent severity but
// open alerts present falls back to "warning".
function severityClass(severity: string | null | undefined): string {
  switch ((severity ?? '').toLowerCase()) {
    case 'critical':
      return 'alert-badge--critical';
    case 'high':
      return 'alert-badge--high';
    case 'medium':
      return 'alert-badge--medium';
    case 'low':
      return 'alert-badge--low';
    default:
      return 'alert-badge--warning';
  }
}

const AlertBadge = ({ summary }: AlertBadgeProps) => {
  const { t } = useTranslation();

  if (!summary || summary.count === 0) {
    return (
      <span className="alert-badge alert-badge--ok" title={t('management.alert.none')}>
        {t('management.alert.ok')}
      </span>
    );
  }

  const title = `${summary.count} ${t('management.alert.open')}${
    summary.types.length ? ` (${summary.types.join(', ')})` : ''
  }`;

  return (
    <span className={`alert-badge ${severityClass(summary.worst_severity)}`} title={title}>
      {summary.worst_severity
        ? t(`management.alert.severity.${summary.worst_severity}`, summary.worst_severity)
        : t('management.alert.open')}
      {summary.count > 1 ? ` (${summary.count})` : ''}
    </span>
  );
};

export default AlertBadge;
